/**
 * AuthContext - 전역 인증 상태 관리
 * 토큰 자동 갱신 및 소셜 로그인 지원
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, safeGetItem, safeSetItem, safeRemoveItem } from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // 로그아웃 처리 (api.js의 auth:logout 이벤트에서 호출)
  const logout = useCallback(() => {
    safeRemoveItem('auth_token');
    safeRemoveItem('refresh_token');
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  // auth:logout 이벤트 리스너
  useEffect(() => {
    const handleLogout = () => {
      logout();
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [logout]);

  // 초기 로드: localStorage에서 토큰 복원 (카카오톡 인앱 브라우저 대응: 쿠키에서도 확인)
  useEffect(() => {
    const initAuth = async () => {
      let token = safeGetItem('auth_token');

      // localStorage에 토큰이 없으면 쿠키에서 확인 (카카오톡 인앱 브라우저 대응)
      if (!token) {
        const match = document.cookie.match(/(^| )auth_token_temp=([^;]+)/);
        if (match) {
          token = decodeURIComponent(match[2]);
          safeSetItem('auth_token', token);
          document.cookie = 'auth_token_temp=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        }
      }

      if (token) {
        try {
          const response = await authAPI.getMe();
          if (response.data.user) {
            setUser(response.data.user);
            setIsAuthenticated(true);
          } else {
            safeRemoveItem('auth_token');
            safeRemoveItem('refresh_token');
          }
        } catch (err) {
          // 네트워크 오류/타임아웃 → 토큰 유지, 인증 상태 유지
          // 서버가 일시적으로 응답 못 하는 경우에도 로그아웃 방지
          if (!err.response || err.code === 'ECONNABORTED') {
            console.warn('Auth init: network/timeout error, keeping auth state');
            setIsAuthenticated(true);
          } else if (err.response?.status === 401) {
            // 401: 토큰 만료 + 리프레시도 실패 (인터셉터가 이미 처리)
            safeRemoveItem('auth_token');
            safeRemoveItem('refresh_token');
          }
          console.error('Auth init error:', err);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // 회원가입
  const register = useCallback(async (email, password, name, nickname) => {
    try {
      setError(null);
      const response = await authAPI.register(email, password, name, nickname);
      return {
        success: true,
        data: response.data,
      };
    } catch (err) {
      const message = err.response?.data?.message || '회원가입 실패';
      setError(message);
      return {
        success: false,
        message,
      };
    }
  }, []);

  // 로그인
  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login(email, password);
      const { token, refresh_token: refreshToken, user: userData } = response.data;

      safeSetItem('auth_token', token);
      if (refreshToken) {
        safeSetItem('refresh_token', refreshToken);
      }
      setUser(userData);
      setIsAuthenticated(true);

      return {
        success: true,
        data: response.data,
      };
    } catch (err) {
      const data = err.response?.data || {};
      const message = data.detail || data.message || '로그인 실패';
      setError(message);
      return {
        success: false,
        message,
        code: data.code,
        needVerification: data.needVerification,
        email: data.email,
      };
    }
  }, []);

  // 소셜 로그인 콜백 (OAuth 리다이렉트 후)
  const handleSocialLoginCallback = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (token) {
      safeSetItem('auth_token', token);
      // 소셜 로그인은 리프레시 토큰도 쿠키나 URL에서 가져올 수 있음
      const refreshToken = params.get('refresh_token');
      if (refreshToken) {
        safeSetItem('refresh_token', refreshToken);
      }

      try {
        const response = await authAPI.getMe();
        if (response.data.user) {
          setUser(response.data.user);
          setIsAuthenticated(true);
          return true;
        }
      } catch (err) {
        console.error('Failed to fetch user after social login:', err);
        // 네트워크 오류 등이면 토큰은 유지하고 사용자 정보만 기본값으로 설정
        if (!err.response) {
          // 네트워크 오류 — 토큰이 유효할 수 있으므로 인증 상태 유지
          setIsAuthenticated(true);
          setUser({ name: params.get('name') || 'User' });
          return true;
        }
        safeRemoveItem('auth_token');
        safeRemoveItem('refresh_token');
        return false;
      }
    }
    return false;
  }, []);

  // 프로필 업데이트
  const updateProfile = useCallback(async (nickname, name) => {
    try {
      setError(null);
      const response = await authAPI.updateProfile(nickname, name);
      if (response.data.user) {
        setUser(response.data.user);
      }
      return {
        success: true,
        data: response.data,
      };
    } catch (err) {
      const message = err.response?.data?.message || '프로필 업데이트 실패';
      setError(message);
      return {
        success: false,
        message,
      };
    }
  }, []);

  // 회원 탈퇴
  const deleteAccount = useCallback(async () => {
    try {
      setError(null);
      await authAPI.deleteAccount();
      safeRemoveItem('auth_token');
      safeRemoveItem('refresh_token');
      setUser(null);
      setIsAuthenticated(false);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || '회원 탈퇴 실패';
      setError(message);
      return { success: false, message };
    }
  }, []);

  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    register,
    login,
    logout,
    deleteAccount,
    updateProfile,
    handleSocialLoginCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
